<template>
  <div>
    <load-profile></load-profile>
    <b-card ref="container" id="container">
      <b-card-text class="mb-2">
        <h5>
          {{ $t("general.customizeChatWidget") }}
        </h5>
      </b-card-text>
      <validation-observer ref="editForm" #default="{ invalid }">
        <n-form-confirmation
          key="editForm"
          @submit="validateForm"
          :form="$refs.editForm"
          :disabled="invalid"
        >
          <b-row align-v="start">
            <b-col md="6"
              ><!-- left side -->
              <n-input :fields="fields" v-model="data" :initValue="initData">
                <template #image="item">
                  <b-form-group
                    :label-for="item.field.key"
                    :description="getDesciption(item.field)"
                    v-if="item.field.type !== 'divider'"
                    :class="item.field.class"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :vid="item.field.key"
                      :name="item.field.label"
                      :rules="item.field.rules"
                    >
                      <label class="d-block">
                        {{ $t(item.field.label) }}
                        <span class="text-muted">
                          - {{ $t("field.optional") }}
                        </span>
                      </label>
                      <n-single-image-uploader
                        :ref="item.field.key"
                        :pw="item.field.pw"
                        :ph="item.field.ph"
                        :fullWidth="item.field.fullWidth"
                        :image="data.brandLogo"
                        v-model="data.image.brandLogo"
                        :readonly="item.field.disabled"
                        @change="onImageChange"
                        :class="errors.length > 0 ? 'is-invalid' : null"
                      >
                      </n-single-image-uploader>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </template>

                <template #imageBot="item">
                  <b-form-group
                    :label-for="item.field.key"
                    :description="getDesciption(item.field)"
                    v-if="item.field.type !== 'divider'"
                    :class="item.field.class"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :vid="item.field.key"
                      :name="item.field.label"
                      :rules="item.field.rules"
                    >
                      <label class="d-block">
                        {{ $t(item.field.label) }}
                        <span class="text-muted">
                          - {{ $t("field.optional") }}
                        </span>
                      </label>
                      <n-single-image-uploader
                        :ref="item.field.key"
                        :pw="item.field.pw"
                        :ph="item.field.ph"
                        :fullWidth="item.field.fullWidth"
                        :image="data.botLogo"
                        v-model="data.image.botLogo"
                        :readonly="item.field.disabled"
                        @change="onImageBotChange"
                        :class="errors.length > 0 ? 'is-invalid' : null"
                      >
                      </n-single-image-uploader>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </template>

                <template #placeholderText="item">
                  <b-form-group
                    :label-for="item.field.key"
                    :description="getDesciption(item.field)"
                    :class="item.field.class"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :vid="item.field.key"
                      :name="item.field.label"
                      :rules="item.field.rules"
                    >
                      <label class="d-block">
                        {{ $t(item.field.label) }}
                        <span class="text-muted">
                          - {{ $t("field.optional") }}
                        </span>
                      </label>
                      <text-editor
                        :key="0"
                        v-model.lazy="initData.placeholderText"
                        @update:textEditor="onUpdateTextEditor"
                        class="mb-2"
                      />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </template>

                <template #color="item">
                  <b-form-group
                    :label-for="item.field.key"
                    :description="getDesciption(item.field)"
                    :class="item.field.class"
                  >
                    <validation-provider
                      #default="{ errors }"
                      :vid="item.field.key"
                      :name="item.field.label"
                      :rules="item.field.rules"
                    >
                      <label class="d-block">
                        {{ $t(item.field.label) }}
                      </label>
                      <n-color-picker
                          :id="item.field.key"
                          :ref="item.field.key"
                          :pickerId="item.field.key"
                          v-model="data.color"
                          :name="item.field.key"
                          :showColorPlaceholder="item.field.showColorPlaceholder"
                          :placeholder="$t(item.field.label)"
                          :errors="errors"
                          :class="errors.length > 0 ? 'is-invalid' : null"
                          @change="onColorChange"
                        />
                      <small class="text-danger">{{ errors[0] }}</small>
                    </validation-provider>
                  </b-form-group>
                </template>
              </n-input>

              <!-- Attachment Icon Section -->
              <div class="mt-3">
                <b-form-group>
                  <label class="d-block">
                    {{ $t("field.attachmentIcon") }}
                  </label>
                  <div class="d-flex" style="gap: 12px; flex-wrap: wrap;">
                    <div
                      v-for="icon in attachmentIconOptions"
                      :key="icon.value"
                      @click="toggleAttachmentIcon(icon.value)"
                      class="attachment-icon-option"
                      :class="{ active: (data.attachmentIcon || 'plus') === icon.value }"
                    >
                      <CameraIcon v-if="icon.value === 'camera'" style="height: 22px; width: 22px;"/>
                      <LinkIcon v-else-if="icon.value === 'link'" style="height: 22px; width: 22px;" />
                      <PhotoIcon v-else-if="icon.value === 'photo'" style="height: 22px; width: 22px;" />
                      <PlusIcon v-else style="height: 22px; width: 22px;" />
                    </div>
                  </div>
                  <small class="text-muted d-block mt-1">
                    {{ $t("message.selectAttachmentIcon") }}
                  </small>
                </b-form-group>
              </div>

              <!-- Chat Shortcuts Section -->
              <div class="mt-2">
                <b-card-text>
                  <div class="d-flex justify-content-between align-items-center mb-1">
                    <h6 class="mb-0">{{ $t("field.chatShortcuts") }}</h6>
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="primary"
                      size="sm"
                      @click="showShortcutModal()"
                    >
                      <feather-icon icon="PlusIcon" size="14" class="mr-50" />
                      {{ $t("button.add") }}
                    </b-button>
                  </div>
                  <small class="text-muted">
                    {{ $t("message.chatShortcutsDescription") }}
                  </small>
                </b-card-text>

                <div v-if="shortcuts.length === 0" class="text-center py-2">
                  <p class="text-muted mb-0">{{ $t("message.noShortcuts") }}</p>
                </div>

                <draggable
                  v-else
                  v-model="shortcuts"
                  class="shortcut-list"
                  ghost-class="ghost"
                  :animation="200"
                  handle=".drag-handle"
                  @end="onShortcutReorder"
                >
                  <b-card
                    v-for="(shortcut, index) in shortcuts"
                    :key="shortcut.id"
                    class="shortcut-item mb-1"
                    no-body
                  >
                    <b-card-body class="p-2">
                      <b-row align-v="center">
                        <b-col cols="auto" class="pr-0">
                          <feather-icon
                            icon="MenuIcon"
                            size="18"
                            class="drag-handle cursor-pointer text-muted"
                          />
                        </b-col>
                        <b-col>
                          <div class="d-flex align-items-center">
                        <img 
                          v-if="isImageIcon(shortcut.labelIcon)" 
                          :src="shortcut.labelIcon" 
                          alt="icon"
                          class="mr-1"
                          style="width: 14.5px; height: 14.5px; object-fit: cover; border-radius: 4px;"
                        />
                        <span v-else class="mr-1">{{ shortcut.labelIcon }}</span>
                            <strong>{{ getShortcutText(shortcut.labelTitle) }}</strong>
                          </div>
                          <small 
                            v-if="shortcut.buttonType === 'quickMessage' && shortcut.labelMessage"
                            :ref="`shortcut-message-${shortcut.id}`"
                            :id="`shortcut-message-${shortcut.id}`"
                            class="text-muted shortcut-text-ellipsis"
                          >
                            <div 
                              v-html="sanitizeHtml(getShortcutText(shortcut.labelMessage))"
                              class="rich-text-content"
                            ></div>
                          </small>
                          <small 
                            v-else-if="shortcut.buttonType === 'link'"
                            :ref="`shortcut-message-${shortcut.id}`"
                            :id="`shortcut-message-${shortcut.id}`"
                            class="text-muted shortcut-text-ellipsis"
                          >
                            <div 
                              v-if="shortcut.labelMessage" 
                              :ref="`shortcut-label-message-${shortcut.id}`"
                              :id="`shortcut-label-message-${shortcut.id}`"
                              class="mb-1 shortcut-text-ellipsis"
                            >
                              <div 
                                v-html="sanitizeHtml(getShortcutText(shortcut.labelMessage))"
                                class="rich-text-content"
                              ></div>
                            </div>
                            <a 
                              v-if="shortcut.urls && shortcut.urls[0]" 
                              :href="shortcut.urls[0]" 
                              target="_blank"
                              rel="noopener noreferrer"
                              class="text-primary"
                              @click.stop
                            >
                              {{ shortcut.urls[0] }}
                            </a>
                          </small>
                          <b-tooltip 
                            v-if="overflowingMessages[shortcut.id]"
                            :target="`shortcut-message-${shortcut.id}`" 
                            placement="top"
                          >
                            <div v-if="shortcut.labelMessage" v-html="sanitizeHtml(getShortcutText(shortcut.labelMessage))"></div>
                            <div v-if="shortcut.buttonType === 'link' && shortcut.urls && shortcut.urls[0]">{{ shortcut.urls[0] }}</div>
                          </b-tooltip>
                          <b-tooltip 
                            v-if="overflowingLabelMessages[shortcut.id]"
                            :target="`shortcut-label-message-${shortcut.id}`" 
                            placement="top"
                          >
                            <div v-html="sanitizeHtml(getShortcutText(shortcut.labelMessage))"></div>
                          </b-tooltip>
                        </b-col>
                        <b-col cols="auto">
                          <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="flat-primary"
                            size="sm"
                            class="btn-icon"
                            @click="showShortcutModal(shortcut)"
                          >
                            <feather-icon icon="EditIcon" size="16" />
                          </b-button>
                          <b-button
                            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                            variant="flat-danger"
                            size="sm"
                            class="btn-icon ml-50"
                            @click="deleteShortcut(shortcut)"
                          >
                            <feather-icon icon="TrashIcon" size="16" />
                          </b-button>
                        </b-col>
                      </b-row>
                    </b-card-body>
                  </b-card>
                </draggable>
              </div>
            </b-col>
            <b-col md="6"
              style="position: sticky; top: 100px;"
              ><!-- right side -->
              <div
                class="chat-widget-container"
                :class="stickyClass"
                :style="{ maxWidth: data.size == 'm' ? `360px` : `330px` }"
              >
                <div
                  class="chat-widget-header"
                  :class="generateTextColor(data.color)"
                  :style="{ backgroundColor: data.color }"
                >
                  <b-row align-v="center">
                    <b-col class="ellipsis">
                      <span
                        class="b-avatar badge-light rounded-circle mr-1"
                        v-show="data.brandLogo"
                      >
                        <span class="b-avatar-img">
                          <img
                            :key="`img-${data.brandLogo}`"
                            :src="data.brandLogo"
                            alt="avatar"
                          />
                        </span>
                      </span>
                      <span
                        class="chat-title"
                        :class="generateTextColor(data.color)"
                      >
                        {{ data.title }}
                      </span>
                    </b-col>
                    <b-col cols="auto">
                      <feather-icon
                        icon="Volume2Icon"
                        size="22"
                        class="mr-1"
                        :class="generateTextColor(data.color)"
                      />
                      <feather-icon
                        icon="ChevronDownIcon"
                        size="22"
                        :class="generateTextColor(data.color)"
                      />
                    </b-col>
                  </b-row>
                </div>
                <div
                  class="chat-widget-body"
                  :style="{ backgroundColor: data.bodyBackgroundColor }"
                >
                  <div class="chats">
                    <div class="chat chat-left">
                      <div>

                        <!-- message `welcome` -->
                        <div class="chat-message">
                          <div class="chat-avatar">
                            <img
                              v-if="data.botLogo"
                              :src="data.botLogo"
                              class="img-fluid rounded-circle"
                              style="object-fit: cover"
                              alt="Avatar"
                            />
                            <span
                              v-else
                              class="b-avatar rounded-circle chat-bubble-box-shadow"
                              :class="generateTextColor(data.color)"
                              style="width: 40px; height: 40px"
                              :style="{ backgroundColor: data.color }"
                            >
                              <span
                                class="b-avatar-custom custom-svg"
                                :style="{ fill: setColor() }"
                              >
                                <svg
                                  width="143px"
                                  height="143px"
                                  viewBox="-337.92 -337.92 1699.84 1699.84"
                                  class="icon"
                                  version="1.1"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M859.4 365.7c-8.9 0-17.5 1.3-25.7 3.7-32-148.2-164.1-259.7-321.7-259.7S222.2 221.2 190.3 369.4c-8.2-2.4-16.8-3.7-25.7-3.7-50.4 0-91.4 41-91.4 91.4v109.7c0 50.4 41 91.4 91.4 91.4s91.4-41 91.4-91.4V438.8c0-141.2 114.8-256 256-256s256 114.8 256 256v109.7c0 92.1-48.9 175.4-127.1 221-16.6-23.1-43.7-38.1-74.2-38.1H457c-50.4 0-91.4 41-91.4 91.4 0 50.4 41 91.4 91.4 91.4h109.7c42.9 0 79-29.8 88.8-69.7 81.6-39.6 141.8-109.7 169.2-193.1 10.7 4.4 22.4 6.8 34.7 6.8 50.4 0 91.4-41 91.4-91.4V457.1c0.1-50.4-41-91.4-91.4-91.4zM182.9 566.9c0 10.1-8.2 18.3-18.3 18.3s-18.3-8.2-18.3-18.3V457.1c0-10.1 8.2-18.3 18.3-18.3s18.3 8.2 18.3 18.3v109.8z m383.8 274.3H457c-10.1 0-18.3-8.2-18.3-18.3 0-10.1 8.2-18.3 18.3-18.3h109.7c10.1 0 18.3 8.2 18.3 18.3 0 10.1-8.2 18.3-18.3 18.3z m311-274.3c0 10.1-8.2 18.3-18.3 18.3s-18.3-8.2-18.3-18.3V457.2c0-10.1 8.2-18.3 18.3-18.3s18.3 8.2 18.3 18.3v109.7z"
                                  ></path>
                                  <path
                                    d="M512 658.3c80.7 0 146.3-65.6 146.3-146.3h-73.1c0 40.3-32.8 73.1-73.1 73.1S439 552.3 439 512h-73.1c-0.2 80.7 65.4 146.3 146.1 146.3z"
                                  ></path>
                                </svg>
                              </span>
                            </span>
                          </div>
                          <div class="chat-body">
                            <div
                              class="chat-content chat-bubble-box-shadow"
                              :class="`${generateTextColor(data.color)}`"
                              :style="{
                                backgroundImage: `linear-gradient(80deg, ${data.color}, ${data.color})`,
                              }"
                            >
                              <p>Hello!, I'm bot.</p>
                            </div>
                          </div>
                        </div>
                        <div
                          class="chat-time"
                          :style="{
                            color: generateContrastColor(data.bodyBackgroundColor || data.color, 0.4)
                          }"
                        >
                          <small>5:17 PM</small>
                        </div>
                      </div>
                    </div>
                    <div class="chat">
                      <div>
                        <!-- message `Hello` right side -->
                        <div class="chat-message">
                          <div class="chat-avatar">
                            <span
                              class="b-avatar rounded-circle chat-bubble-box-shadow"
                              style="width: 40px; height: 40px"
                              :class="generateTextColor(data.color)"
                              :style="{ backgroundColor: data.color }"
                            >
                              <span class="b-avatar-custom">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="22px"
                                  height="22px"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  class="feather feather-user"
                                >
                                  <path
                                    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                                  />
                                  <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                              </span>
                            </span>
                          </div>
                          <div class="chat-body">
                            <div
                              class="chat-content chat-bubble-box-shadow"
                              :class="`${generateTextColor(data.color)}`"
                              :style="{
                                backgroundImage: `linear-gradient(80deg, ${data.color}, ${data.color})`,
                              }"
                            >
                              <p>Hello</p>
                            </div>
                          </div>
                        </div>
                        <div
                          class="chat-time"
                          :style="{
                            color: generateContrastColor(data.bodyBackgroundColor || data.color, 0.4)
                          }"
                        >
                          <small>5:17 PM</small>
                        </div>
                      </div>
                    </div>
                    <div v-if="data.defaultMessage" class="chat chat-left">
                      <div>
                        <div class="chat-message">
                          <div class="chat-avatar">
                            <img
                              v-if="data.brandLogo"
                              :src="data.brandLogo"
                              class="img-fluid rounded-circle"
                              style="object-fit: cover"
                              alt="Avatar"
                            />
                            <span
                              v-else
                              class="b-avatar rounded-circle text-dark chat-bubble-box-shadow"
                              style="width: 40px; height: 40px"
                              :style="{ backgroundColor: '#dcdcdc' }"
                            >
                              <span class="b-avatar-custom"
                                ><svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="22px"
                                  height="22px"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  class="feather feather-user"
                                >
                                  <path
                                    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                                  />
                                  <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                              </span>
                            </span>
                          </div>
                          <div class="chat-body">
                            <div class="chat-content chat-bubble-box-shadow">
                              <p class="pre-formatted">
                                {{ data.defaultMessage }}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div
                          class="chat-time"
                          :style="{
                            color: generateContrastColor(data.bodyBackgroundColor || data.color, 0.4)
                          }"
                        >
                          <small>5:17 PM</small>
                        </div>
                      </div>
                    </div>
                    <div class="chat chat-left">
                      <div>
                        <!-- message `Hello` left side -->
                        <div class="chat-message">
                          <div class="chat-avatar">
                            <img
                              v-if="userData.profilePicture"
                              :src="userData.profilePicture"
                              class="img-fluid rounded-circle"
                              style="object-fit: cover"
                              alt="Avatar"
                            />
                            <span
                              v-else
                              class="b-avatar rounded-circle text-dark chat-bubble-box-shadow"
                              style="width: 40px; height: 40px"
                              :style="{ backgroundColor: data.color }"
                            >
                              <span
                                class="b-avatar-custom"
                                :class="generateTextColor(data.color)"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="22px"
                                  height="22px"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  class="feather feather-user"
                                >
                                  <path
                                    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                                  />
                                  <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                              </span>
                            </span>
                          </div>
                          <div class="chat-body">
                            <div
                              class="chat-content chat-bubble-box-shadow"
                              :class="generateTextColor(data.color)"
                              :style="{
                                backgroundImage: `linear-gradient(80deg, ${data.color}, ${data.color})`,
                              }"
                            >
                              <p>Hello</p>
                            </div>
                          </div>
                        </div>
                        <div
                          class="chat-time"
                          :style="{
                            color: generateContrastColor(data.bodyBackgroundColor || data.color, 0.4)
                          }"
                        >
                          <small>5:18 PM</small>
                        </div>
                      </div>
                    </div>
                    <div v-if="data.typeIndicator" class="chat chat-left">
                      <div>
                        <div class="chat-message">
                          <div class="chat-avatar">
                            <img
                              v-if="userData.profilePicture"
                              :src="userData.profilePicture"
                              class="img-fluid rounded-circle"
                              style="object-fit: cover"
                              alt="Avatar"
                            />
                            <span
                              v-else
                              class="b-avatar rounded-circle text-dark chat-bubble-box-shadow"
                              style="width: 40px; height: 40px"
                              :style="{ backgroundColor: data.color }"
                            >
                              <span
                                class="b-avatar-custom"
                                :class="generateTextColor(data.color)"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="22px"
                                  height="22px"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  stroke-width="2"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  class="feather feather-user"
                                >
                                  <path
                                    d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                                  ></path>
                                  <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                              </span>
                            </span>
                          </div>
                          <div class="chat-body">
                            <div
                              class="chat-content chat-bubble-box-shadow"
                              :style="{
                                backgroundImage: `linear-gradient(80deg, ${data.color}, ${data.color})`,
                              }"
                            >
                              <div class="typing">
                                <div
                                  :class="`dot bg-${generateVariantColor(
                                    data.color
                                  )}`"
                                ></div>
                                <div
                                  :class="`dot bg-${generateVariantColor(
                                    data.color
                                  )}`"
                                ></div>
                                <div
                                  :class="`dot bg-${generateVariantColor(
                                    data.color
                                  )}`"
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  class="chat-widget-footer"
                  :style="{
                    backgroundColor: data.bodyBackgroundColor,
                  }"
                >
                  <!-- Shortcuts Preview -->
                  <div
                    v-if="shortcuts.length > 0"
                    class="chat-shortcuts-container"
                    :style="{
                      padding: '8px 0 0 0',
                      borderTop: `1px solid ${generateContrastColor(data.bodyBackgroundColor || data.color, 0.1)}`,
                      overflowX: 'auto',
                      overflowY: 'hidden',
                    }"
                  >
                    <div 
                      class="d-flex"
                      :style="{
                        gap: '6px',
                        flexWrap: 'nowrap',
                        paddingBottom: '8px'
                      }"
                    >
                      <a
                        v-for="shortcut in shortcuts"
                        :key="shortcut.id"
                        class="shortcut-button"
                        :style="{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          backgroundColor: hexToRgbA(data.color, 0.1),
                          color: data.color,
                          borderRadius: '16px',
                          fontSize: data.size === 's' ? '0.75rem' : '0.8rem',
                          border: `1px solid ${hexToRgbA(data.color, 0.2)}`,
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          textDecoration: 'none',
                          cursor: 'auto'
                        }"
                      >
                        <img 
                          v-if="isImageIcon(shortcut.labelIcon)" 
                          :src="shortcut.labelIcon" 
                          alt="icon"
                          style="width: 12px; height: 12px; object-fit: cover; border-radius: 4px;"
                        />
                        <span v-else>{{ shortcut.labelIcon }}</span>
                        <span style="font-weight: 500;">{{ getShortcutText(shortcut.labelTitle) }}</span>
                      </a>
                    </div>
                  </div>

                  <!-- Input Section -->
                  <div
                    :style="{
                      padding: '12px 0',
                      display: 'flex',
                      alignItems: 'center'
                    }"
                  >
                    <b-input-group class="input-group-merge">
                    <b-input-group-prepend v-if="!text" style="height: 40px;">
                      <div
                        class="input-group-text"
                        :style="{
                          borderColor: borderColor,
                          borderRight: 0,
                          borderTopRightRadius: 0,
                          borderBottomRightRadius: 0,
                          height: '100%'
                        }"
                      >
                        <CameraIcon v-if="data.attachmentIcon === 'camera'" />
                        <LinkIcon v-else-if="data.attachmentIcon === 'link'" />
                        <PhotoIcon v-else-if="data.attachmentIcon === 'photo'" />
                        <PlusIcon v-else />
                      </div>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="text"
                      class="form-control-merge"
                      :placeholder="initData.placeholderText[locale]"
                      :style="{
                        borderColor: borderColor,
                        color: generateContrastColor(data.bodyBackgroundColor || data.color, 0.4),
                        fontSize: data.size === 's' ? '0.9rem' : '1rem'
                      }"
                      @blur="borderColor = generateContrastColor(data.bodyBackgroundColor || data.color, 0.4)"
                      @focus="borderColor = data.color"
                    />
                    <b-input-group-append style="height: 40px;">
                      <div
                        class="input-group-text"
                        :style="{ borderColor: borderColor }"
                      >
                        <feather-icon
                          class="cursor-pointer"
                          icon="SmileIcon"
                          size="18"
                          :style="{
                            color: generateContrastColor(data.bodyBackgroundColor || data.color, 0.4)
                          }"
                        />
                      </div>
                    </b-input-group-append>
                  </b-input-group>
                  <div
                    class="chat-bubble-box-shadow"
                    :style="{
                      height: '36px',
                      width: '36px',
                      borderRadius: '50%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: data.color,
                      flexShrink: 0,
                      marginLeft: '15px',
                      color: generateContrastColor(data.color)
                    }"
                  >
                    <feather-icon
                      v-if="!text"
                      class="cursor-pointer"
                      icon="MicIcon"
                      size="18"
                      :style="{
                        color: generateContrastColor(data.color)
                      }"
                    />
                    <feather-icon
                      v-else
                      class="cursor-pointer"
                      icon="SendIcon"
                      size="18"
                      :style="{
                        color: generateContrastColor(data.color)
                      }"
                    />
                  </div>
                  </div>
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12" class="text-right">
              <n-button-loading
                v-if="$can('update', permissionType)"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                class="mt-1"
                :loading="loading"
                :disabled="invalid"
              >
                {{ $t("button.saveChanges") }}
              </n-button-loading>
            </b-col>
          </b-row>
        </n-form-confirmation>
      </validation-observer>
    </b-card>

    <!-- Shortcut Modal -->
    <b-modal
      ref="shortcutModal"
      id="shortcut-modal"
      :title="editingShortcut ? $t('title.editShortcut') : $t('title.addShortcut')"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <validation-observer ref="shortcutForm" v-slot="{ invalid }">
        <b-form @submit.prevent="saveShortcut">
          <b-row>
            <!-- Button Type -->
            <b-col cols="12">
              <b-form-group :label="$t('field.buttonType')">
                <b-form-radio-group
                  v-model="shortcutForm.buttonType"
                  :options="buttonTypeOptions"
                  class="mb-0"
                />
              </b-form-group>
            </b-col>

            <!-- Icon -->
            <b-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('field.buttonIcon')"
              >
                <b-form-group :label="$t('field.buttonIcon')">
                  <!-- Icon Type Selection -->
                  <b-form-radio-group
                    v-model="shortcutForm.iconType"
                    :options="iconTypeOptions"
                    class="mb-2"
                  />
                  
                  <!-- Text Emoji Input -->
                  <div v-if="shortcutForm.iconType === 'text'">
                    <b-form-input
                      v-model="shortcutForm.labelIcon"
                      :placeholder="$t('placeholder.icon')"
                      :state="errors.length > 0 ? false : null"
                      maxlength="2"
                    />
                    <small class="text-muted">{{ $t('message.emojiOnly') }}</small>
                  </div>
                  
                  <!-- Image Upload -->
                  <div v-else>
                    <div class="mb-1">
                      <n-single-image-uploader
                        ref="shortcutIconUploader"
                        :ph="150"
                        :pw="150"
                        :fullWidth="false"
                        :image="shortcutForm.iconImageUrl"
                        v-model="shortcutForm.iconImage"
                        @change="onShortcutIconChange"
                        :class="errors.length > 0 ? 'is-invalid' : null"
                      />
                    </div>
                    <small class="text-muted d-block">{{ $t('general.recommendDimension', { ph: 48, pw: 48 }) }}</small>
                  </div>
                  
                  <small class="text-danger d-block">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Title -->
            <b-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('field.buttonName')"
                rules="max:50"
              >
                <b-form-group :label="$t('field.buttonName')">
                  <div class="d-flex" style="gap: 10px;">
                    <b-form-input
                      v-model="shortcutForm.labelTitle[shortcutLocale]"
                      :placeholder="$t('placeholder.enterButtonName')"
                      :state="errors.length > 0 ? false : null"
                    />
                    <b-form-select
                      v-model="shortcutLocale"
                      :options="localeOptions"
                      style="width: auto;"
                    />
                  </div>
                  <small class="text-muted">{{ $t('message.titleHint') }}</small>
                  <small class="text-danger d-block">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

            <!-- Button Message (only for quickMessage type) -->
            <b-col cols="12" v-if="shortcutForm.buttonType === 'quickMessage'">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('general.pop_up_content')"
                rules="max:200"
              >
                <b-form-group :label="$t('general.pop_up_content')">
                  <rich-text-editor
                    v-model="shortcutForm.labelMessage"
                    :placeholder="$t('general.enter_pop_up_content')"
                    :initial-locale="shortcutLocale"
                    :class="errors.length > 0 ? 'is-invalid' : null"
                  />
                  <small class="text-muted">{{ $t('general.pop_up_content_hint') }}</small>
                  <small class="text-danger d-block">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
            
            <!-- URL (only for link type) -->
            <b-col cols="12" v-if="shortcutForm.buttonType === 'link'">
              <validation-provider
                v-slot="{ errors }"
                :name="$t('field.buttonLink')"
                rules="url|max:500"
              >
                <b-form-group :label="$t('field.buttonLink')">
                  <b-form-input
                    v-model="shortcutForm.url"
                    :placeholder="$t('placeholder.enterButtonLink')"
                    :state="errors.length > 0 ? false : null"
                  />
                  <small class="text-muted">{{ $t('field.linkHint') }}</small>
                  <small class="text-danger d-block">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>

          </b-row>

          <b-row class="mt-2">
            <b-col class="text-right">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="outline-secondary"
                @click="$refs.shortcutModal.hide()"
                class="mr-1"
              >
                {{ $t("button.cancel") }}
              </b-button>
              <n-button-loading
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                type="submit"
                variant="primary"
                :loading="shortcutLoading"
                :disabled="invalid || !isShortcutFormValid()"
              >
                {{ $t("button.save") }}
              </n-button-loading>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardText,
  BLink,
  BAvatar,
  BInputGroup,
  BInputGroupAppend,
  BFormGroup,
  BFormInput,
  BInputGroupPrepend,
  BButton,
  BModal,
  BForm,
  BFormTextarea,
  BFormSelect,
  BFormRadioGroup,
  BCardBody,
  BTooltip,
} from "bootstrap-vue";
import Ripple from "vue-ripple-directive";
import NButtonLoading from "@/components/NButtonLoading";
import NFormConfirmation from "@/components/NFormConfirmation";
import NInput from "@/components/NInput";
import FormInput from "./formInput";
import NSingleImageUploader from "@/components/NSingleImageUploader";
import {
  generateContrastColor,
  generateTextColor,
  generateVariantColor,
  hexToRgbA,
  rgbaToHex
} from "@/libs/helper";
import useJwt from "@/auth/jwt/useJwt";
import Repository from "@/repositories/RepositoryFactory";
import TextEditor from "./TextEditor.vue";
import RichTextEditor from "./RichTextEditor.vue";
import NColorPicker from "@/components/NColorPicker";
import { v4 } from "uuid";
import draggable from "vuedraggable";
import DOMPurify from "dompurify";
import CameraIcon from "@/components/icons/AttachmentIcons/CameraIcon.vue";
import LinkIcon from "@/components/icons/AttachmentIcons/LinkIcon.vue";
import PlusIcon from "@/components/icons/AttachmentIcons/PlusIcon.vue";
import PhotoIcon from "@/components/icons/AttachmentIcons/PhotoIcon.vue";

const ChatWidgetRepository = Repository.get("chatWidget");
const FileRepository = Repository.get("file");
const ShortcutRepository = Repository.get("shortcutRepo");

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BLink,
    BAvatar,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupPrepend,
    BFormGroup,
    BFormInput,
    BButton,
    BModal,
    BForm,
    BFormTextarea,
    BFormSelect,
    BFormRadioGroup,
    BCardBody,
    BTooltip,
    TextEditor,
    RichTextEditor,
    NButtonLoading,
    NFormConfirmation,
    NInput,
    NSingleImageUploader,
    NColorPicker,
    draggable,
    CameraIcon,
    LinkIcon,
    PlusIcon,
    PhotoIcon,
  },
  directives: {
    Ripple,
  },
  computed: {
    iconTypeOptions() {
      return [
        { value: 'text', text: this.$t('field.textOrEmoji') },
        { value: 'image', text: this.$t('field.icon') },
      ];
    },
    buttonTypeOptions() {
      return [
        { value: 'quickMessage', text: this.$t('general.internal_pop_up') },
        { value: 'link', text: this.$t('field.externalLink') },
      ];
    },
    userData: function () {
      return this.$store.state.profile?.email || this.$store.state.profile?.phone
        ? this.$store.state.profile
        : JSON.parse(
            localStorage.getItem(useJwt.jwtConfig.storageUserDataKeyName)
          );
    },
    locale() {
      return this.$i18n.locale.toLowerCase().split("-")[0];
    },
  },
  data() {
    return {
      data: {
        title: "Message us",
        logo: { brandLogo: null, botLogo: null },
        image: {},
        color: "#8d5185",
        bodyBackgroundColor: '',
        size: "s",
        typeIndicator: true,
        defaultMessage: "",
        attachmentIcon: "",
        placeholderText: {
          en: null,
          zh: null,
          km: null,
          vi: null,
        },
      },
      attachmentIconOptions: [
        { value: "plus", icon: "📷" },
        { value: "link", icon: "📎" },
        { value: "camera", icon: "🏞️" },
        { value: "photo", icon: "╋" },
      ],
      borderColor: null,
      initData: {
        placeholderText: {
          en: null,
          zh: null,
          km: null,
          vi: null,
        },
      },
      file: null,
      loading: false,
      isSticky: false,
      stickyClass: "",
      chatPosTop: 0,
      text: "",

      // Shortcuts
      shortcuts: [],
      shortcutLoading: false,
      editingShortcut: null,
      shortcutLocale: 'en',
      overflowingMessages: {},
      overflowingLabelMessages: {},
      shortcutForm: {
        iconType: 'text', // 'text' or 'image'
        labelIcon: '',
        iconImage: null,
        iconImageUrl: null,
        labelTitle: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        labelMessage: {
          en: '',
          zh: '',
          km: '',
          vi: '',
        },
        buttonType: 'quickMessage', // 'quickMessage' or 'link'
        url: '',
      },
      localeOptions: [
        { value: 'en', text: 'English' },
        { value: 'zh', text: '中文' },
        { value: 'km', text: 'ភាសាខ្មែរ' },
        { value: 'vi', text: 'Tiếng Việt' },
      ],

      generateTextColor,
      generateVariantColor,
      generateContrastColor,
      hexToRgbA,
    };
  },
  watch: {
    'data.color': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.borderColor = generateContrastColor(this.data.bodyBackgroundColor || this.data.color, 0.4)
      }
    },
    'data.bodyBackgroundColor': function (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.borderColor = generateContrastColor(this.data.bodyBackgroundColor || this.data.color, 0.4)
      }
    },
  },
  mounted() {
    this.show();
    this.loadShortcuts();
    let chat = document.getElementById("container");
    this.chatPosTop = chat.getBoundingClientRect().top;
    window.addEventListener("scroll", this.handleScroll);
    this.borderColor = this.generateContrastColor(this.data.color, 0.4)
  },
  updated() {
    this.checkOverflowingMessages();
  },
  destroyed() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    onUpdateTextEditor(data) {
      this.data.placeholderText = data;
    },
    setColor() {
      let color = this.generateVariantColor(this.data.color);
      return color == "light" ? "#fff" : "#4b4b4b";
    },
    getDesciption(field) {
      let image = ["single-image", "async-single-image"];
      if (image.includes(field.type)) {
        return this.$t("general.recommendDimension", {
          ph: field.ph,
          pw: field.pw,
        });
      } else if (field.hint) {
        return field.hint;
      }
      return "";
    },
    handleScroll(event) {
      let scrollPos = window.scrollY;
      if (scrollPos > this.chatPosTop) {
        this.stickyClass = "sticky";
      } else {
        this.stickyClass = "";
      }
    },
    onImageChange(file) {
      this.data.brandLogo = file;
    },
    onImageBotChange(file) {
      this.data.botLogo = file;
    },
    show() {
      this.loading = true;
      ChatWidgetRepository.show()
        .then((response) => {
          const data = response?.data?.data;
          if (data) {
            const placeholder = {
              en: null,
              zh: null,
              km: null,
              vi: null,
            };
            if(data?.bodyBackgroundColor == null) {
              data.bodyBackgroundColor = rgbaToHex(hexToRgbA(data.color));
            }
            if(!data?.attachmentIcon) {
              data.attachmentIcon = "";
            }
            this.initData = {
              ...data,
            };
            this.data.attachmentIcon = data.attachmentIcon || "";
            if (!this.initData?.placeholderText) {
              this.initData.placeholderText = placeholder;
            }
          }
        })
        .catch()
        .then(() => {
          this.loading = false;
        });
    },
    validateForm() {
      this.$refs.editForm.validate().then(async (success) => {
        if (success) {
          const isImageEmpty = Object.keys(this.data.image).length === 0;
          if (!isImageEmpty) {
            const { brandLogo, botLogo } = this.data.image;
            try {
              if (brandLogo) {
                try {
                  const response = await this.uploadImage(brandLogo);
                  const dataMediaId = response?.data?.data;
                  this.data.brandLogoMediaId = dataMediaId.mediaId;
                } catch (error) {
                  this.loading = false;
                }
              }

              if (botLogo) {
                try {
                  const response = await this.uploadImage(botLogo);
                  const dataMediaId = response?.data?.data;
                  this.data.botLogoMediaId = dataMediaId.mediaId;
                } catch (error) {
                  this.loading = false;
                }
              }
            } catch (error) {
              this.loading = false;
            }
            this.submit();
          } else {
            this.submit();
          }
        }
      });
    },
    async uploadImage(fileLogo) {
      this.loading = true;
      let formData = new FormData();
      formData.append("image", fileLogo);
      return await FileRepository.upload(formData);
    },
    toggleAttachmentIcon(iconValue) {
      if (this.data.attachmentIcon === iconValue) {
        this.data.attachmentIcon = "";
      } else {
        this.data.attachmentIcon = iconValue;
      }
    },
    submit() {
      this.loading = true;
      ChatWidgetRepository.update({
        title: this.data.title,
        brandLogoMediaId: this.data.brandLogoMediaId,
        botLogoMediaId: this.data.botLogoMediaId,
        color: this.data.color,
        bodyBackgroundColor: this.data.bodyBackgroundColor,
        size: this.data.size,
        hoverText: this.data.hoverText,
        typeIndicator: this.data.typeIndicator,
        defaultMessage: this.data.defaultMessage,
        attachmentIcon: this.data.attachmentIcon,
        placeholderText: this.data.placeholderText,
      })
        .then((response) => {})
        .catch(() => {
          this.loading = false;
        })
        .then(() => {
          this.loading = false;
        });
    },
    back() {
      this.$router.back();
    },
    onColorChange(color) {
      // this.data.bodyBackgroundColor = rgbaToHex(hexToRgbA(color));
    },

    // Shortcut Methods
    loadShortcuts() {
      ShortcutRepository.list({ page: 1, limit: 100 })
        .then((response) => {
          console.log('Shortcuts response:', response);
          const data = response?.data?.data?.list || response?.data?.data || [];
          console.log('Shortcuts data:', data);
          this.shortcuts = Array.isArray(data) ? data.sort((a, b) => (a.sorting || 0) - (b.sorting || 0)) : [];
          this.checkOverflowingMessages();
        })
        .catch((error) => {
          console.error('Error loading shortcuts:', error);
        });
    },

    showShortcutModal(shortcut = null) {
      this.editingShortcut = shortcut;
      this.shortcutLocale = this.locale;
      
      if (shortcut) {
        // Check if labelIcon is an image URL (starts with http or contains image extensions)
        const isImageUrl = shortcut.labelIcon && (
          shortcut.labelIcon.startsWith('http') || 
          /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(shortcut.labelIcon)
        );
        
        // Determine buttonType: if has urls, it's a link, otherwise quickMessage
        const hasUrl = shortcut.urls && shortcut.urls[0] && shortcut.urls[0].trim() !== '';
        const buttonType = hasUrl ? 'link' : (shortcut.buttonType || 'quickMessage');
        
        this.shortcutForm = {
          iconType: isImageUrl ? 'image' : 'text',
          labelIcon: isImageUrl ? '' : (shortcut.labelIcon || ''),
          iconImage: null,
          iconImageUrl: isImageUrl ? shortcut.labelIcon : null,
          labelTitle: { ...shortcut.labelTitle },
          labelMessage: { ...(shortcut.labelMessage || { en: '', zh: '', km: '', vi: '' }) },
          buttonType: buttonType,
          url: shortcut.urls && shortcut.urls[0] ? shortcut.urls[0] : '',
        };
      } else {
        this.shortcutForm = {
          iconType: 'text',
          labelIcon: '',
          iconImage: null,
          iconImageUrl: null,
          labelTitle: { en: '', zh: '', km: '', vi: '' },
          labelMessage: { en: '', zh: '', km: '', vi: '' },
          buttonType: 'quickMessage',
          url: '',
        };
      }
      
      this.$refs.shortcutModal.show();
    },

    onShortcutIconChange(file) {
      this.shortcutForm.iconImageUrl = file;
    },

    isImageIcon(icon) {
      if (!icon) return false;
      return icon.startsWith('http') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(icon);
    },

    getShortcutText(textObj) {
      if (!textObj) return '';
      return textObj?.[this.locale] || textObj?.en || textObj?.zh || textObj?.km || textObj?.vi || '-';
    },

    sanitizeHtml(html) {
      if (!html) return '';
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'span', 'div'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class'],
        ALLOWED_STYLES: {
          '*': {
            'color': [/^#[0-9A-Fa-f]{3,6}$/, /^rgb/, /^rgba/],
            'background-color': [/^#[0-9A-Fa-f]{3,6}$/, /^rgb/, /^rgba/],
            'text-align': [/^left$/, /^right$/, /^center$/, /^justify$/],
          },
        },
      });
    },

    checkOverflowingMessages() {
      this.$nextTick(() => {
        const newOverflowingMessages = {};
        const newOverflowingLabelMessages = {};
        this.shortcuts.forEach((shortcut) => {
          // Check main message container
          const refName = `shortcut-message-${shortcut.id}`;
          const element = this.$refs[refName];
          if (element && element.length) {
            const el = element[0];
            newOverflowingMessages[shortcut.id] = el.scrollHeight > el.clientHeight;
          }
          
          // Check labelMessage specifically for link type shortcuts
          if (shortcut.buttonType === 'link' && shortcut.labelMessage) {
            const labelRefName = `shortcut-label-message-${shortcut.id}`;
            const labelElement = this.$refs[labelRefName];
            if (labelElement && labelElement.length) {
              const labelEl = labelElement[0];
              newOverflowingLabelMessages[shortcut.id] = labelEl.scrollHeight > labelEl.clientHeight;
            }
          }
        });
        this.overflowingMessages = newOverflowingMessages;
        this.overflowingLabelMessages = newOverflowingLabelMessages;
      });
    },

    async saveShortcut() {
      this.$refs.shortcutForm.validate().then(async (success) => {
        if (success) {
          this.shortcutLoading = true;
          
          try {
            const payload = {
              labelTitle: this.shortcutForm.labelTitle,
              buttonType: this.shortcutForm.buttonType,
            };

            // Only include urls if buttonType is 'link'
            if (this.shortcutForm.buttonType === 'link') {
              payload.urls = [this.shortcutForm.url];
            } else {
              payload.urls = [];
              // Include labelMessage for quickMessage type
              payload.labelMessage = this.shortcutForm.labelMessage;
            }

            // Handle icon based on type
            if (this.shortcutForm.iconType === 'image') {
              if (this.shortcutForm.iconImage) {
                // Upload new icon image and use URL as labelIcon
                const response = await this.uploadImage(this.shortcutForm.iconImage);
                const data = response?.data?.data;
                payload.labelIcon = data.url; // Use image URL as labelIcon
              } else if (this.shortcutForm.iconImageUrl) {
                // Keep existing image URL
                payload.labelIcon = this.shortcutForm.iconImageUrl;
              }
            } else {
              // Text/emoji type
              payload.labelIcon = this.shortcutForm.labelIcon;
            }

            const request = this.editingShortcut
              ? ShortcutRepository.update(this.editingShortcut.id, payload)
              : ShortcutRepository.create(payload);

            await request;
            this.$refs.shortcutModal.hide();
            this.loadShortcuts();
          } catch (error) {
            console.error('Error saving shortcut:', error);
          } finally {
            this.shortcutLoading = false;
          }
        }
      });
    },

    isShortcutFormValid() {
      // Validate icon - must have either text/emoji or image
      if (this.shortcutForm.iconType === 'text') {
        if (!this.shortcutForm.labelIcon || this.shortcutForm.labelIcon.trim() === '') {
          return false;
        }
      } else {
        // Image type
        if (!this.shortcutForm.iconImage && !this.shortcutForm.iconImageUrl) {
          return false;
        }
      }

      // Validate title - at least one locale must have a value
      const hasTitle = Object.values(this.shortcutForm.labelTitle).some(
        val => val && val.trim() !== ''
      );
      if (!hasTitle) {
        return false;
      }

      // Validate URL - only required when buttonType is 'link'
      if (this.shortcutForm.buttonType === 'link') {
        if (!this.shortcutForm.url || this.shortcutForm.url.trim() === '') {
          return false;
        }
      } else {
        // Validate labelMessage - required when buttonType is 'quickMessage'
        const hasMessage = Object.values(this.shortcutForm.labelMessage).some(
          val => val && val.trim() !== ''
        );
        if (!hasMessage) {
          return false;
        }
      }

      return true; // All validations passed
    },

    deleteShortcut(shortcut) {
      this.$bvModal
        .msgBoxConfirm(this.$t("alert.deleteShortcut"), {
          title: this.$t("alert.areYouSure"),
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: this.$t("button.yesDelete"),
          cancelTitle: this.$t("button.cancel"),
          cancelVariant: "outline-secondary",
          hideHeaderClose: false,
          centered: true,
        })
        .then((value) => {
          if (value) {
            ShortcutRepository.delete(shortcut.id)
              .then(() => {
                this.loadShortcuts();
              })
              .catch((error) => {
                console.error('Error deleting shortcut:', error);
              });
          }
        })
        .catch((error) => {
          console.error('Error in delete confirmation:', error);
        });
    },

    onShortcutReorder() {
      const payload = this.shortcuts.map((shortcut, index) => ({
        id: shortcut.id,
        sorting: index,
      }));

      ShortcutRepository.reorder(payload)
        .catch((error) => {
          console.error('Error reordering shortcuts:', error);
          this.loadShortcuts();
        });
    },
  },
  setup() {
    let fields = FormInput;
    const permissionType = "customize-chat-widget";

    return { fields, permissionType };
  },
};
</script>

<style lang="scss" scoped>
.shortcut-list {
  .shortcut-item {
    transition: all 0.3s ease;
    border: 1px solid rgba(0, 0, 0, 0.1);
    
    &:hover {
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
  
  .drag-handle {
    cursor: grab;
    
    &:active {
      cursor: grabbing;
    }
  }
  
  .ghost {
    opacity: 0.5;
    background: #f8f8f8;
  }
}

.shortcut-text-ellipsis {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  
  .rich-text-content {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    
    ::v-deep p,
    ::v-deep h1,
    ::v-deep h2,
    ::v-deep h3,
    ::v-deep h4,
    ::v-deep h5,
    ::v-deep h6 {
      margin-bottom: 0.25rem;
      line-height: 1.4;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
    
    ::v-deep ul,
    ::v-deep ol {
      margin-bottom: 0.25rem;
      padding-left: 1.25rem;
    }
    
    ::v-deep a {
      color: inherit;
      text-decoration: underline;
    }
  }
}

.chat-shortcuts-container {
  /* Hide scrollbar for webkit browsers */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Hide scrollbar for Firefox */
  scrollbar-width: none;
  
  /* Hide scrollbar for IE, Edge */
  -ms-overflow-style: none;
  
  .shortcut-button {
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      opacity: 0.9;
    }
  }
}

.attachment-icon-option {
  cursor: pointer;
  padding: 10px 16px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
  transition: all 0.2s;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 60px;
  min-height: 50px;

  &:hover {
    // transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &.active {
    font-weight: bold;
    border-color: #9f6096;
    background-color: rgba(159, 96, 150, 0.1);
  }
}
</style>

<style>
/* #shortcut-modal {
  @media (min-width: 576px) {
    .modal-dialog {
        max-width: 600px;
    }
  }
} */
</style>